import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useNavigate } from 'react-router';
import { PersonAdd } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, List, Stack, Typography } from '@mui/material';

import FloatingBottomButton from '@/components/common/FloatingBottomButton';
import SearchNoResult from '@/shared/ui/no-result-display/SearchNoResult';
import { SearchComponent } from '@/components/common/Search';
import { SortDrawer, type SortValue } from '@/components/common/SortDrawer';

import { useInfiniteUserList } from '../../api/userQueries';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { canManageUsers } from '../../utils/userAccessControlPermissions';
import { userAccessRoutes } from '../../utils/userAccessRoutes';
import { filterDeactivatedUsers, getUserListFeedback, shouldShowWelcomeCard } from '../../utils/userListUtils';
import { UserAccessErrorState } from '../user-access-state-panel/UserAccessErrorState';
import { UserFilterDrawer } from '../user-filter-drawer/UserFilterDrawer';
import { UserListItem } from '../user-list-item/UserListItem';
import { UserListHeader } from './UserListHeader';
import { UserListWelcomeCard } from './UserListWelcomeCard';
import { userListSx } from './UserList.styles';

const PAGE_SIZE = 10;

export function UserList() {
  const userCtx = useCurrentUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [sort, setSort] = useState<SortValue[]>([]);
  const { users, hasMore, isLoading, isFetchingNextPage, isError, loadMoreUsers, retryUsers } = useInfiniteUserList(
    {
      filter: searchText,
      order: sort[0]?.direction ?? 'asc',
    },
    PAGE_SIZE
  );
  const isAllowedToAddUser = canManageUsers(userCtx?.user);
  const visibleUsers = filterDeactivatedUsers(users, showDeactivated);
  const { showBackgroundError, showManualLoadMore } = getUserListFeedback({
    hasMore,
    isError,
    isFetchingNextPage,
    loadedUserCount: users.length,
    visibleUserCount: visibleUsers.length,
  });
  const showWelcomeCard = shouldShowWelcomeCard({
    isLoading,
    hasMore,
    searchText,
    users,
    currentUserId: userCtx?.user.id,
    canAddUser: isAllowedToAddUser,
  });

  function handleLoadMore() {
    if (isFetchingNextPage || isError) {
      return;
    }
    void loadMoreUsers();
  }

  function handleRetry() {
    void retryUsers();
  }

  const header = <UserListHeader />;

  if (isError && users.length === 0) {
    return <UserAccessErrorState header={header} onRetry={handleRetry} />;
  }

  return (
    <Stack position="relative" p={1} height="100%" minHeight={0}>
      {header}
      <SearchComponent
        searchText={searchText}
        setSearchText={setSearchText}
        placeholder={t('userAccessControl.searchPlaceholder')}
        showHistory={false}
      />
      {showBackgroundError && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              {t('error.dialog.retry')}
            </Button>
          }
        >
          {t('userAccessControl.errors.refreshFailed')}
        </Alert>
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between" minHeight={40}>
        <Typography
          variant="body2"
          color="text.secondary"
          px={1}
          aria-live="polite"
          aria-label={t('userAccessControl.userCount', { count: visibleUsers.length })}
        >
          ({visibleUsers.length})
        </Typography>
        <Stack direction="row">
          <SortDrawer
            options={[{ field: 'name', label: t('userAccessControl.sort.name') }]}
            value={sort}
            onChange={setSort}
            title={t('userAccessControl.sort.title')}
          />
          <UserFilterDrawer showDeactivated={showDeactivated} onShowDeactivatedChange={setShowDeactivated} />
        </Stack>
      </Stack>
      <List component="div" role="list" sx={userListSx}>
        {isLoading && users.length === 0 && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
        <InfiniteScroll
          pageStart={0}
          initialLoad={false}
          loadMore={handleLoadMore}
          hasMore={hasMore}
          useWindow={false}
          loader={
            <Box key="user-list-loader" display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          }
        >
          {visibleUsers.map((user) => (
            <UserListItem key={user.id} user={user} />
          ))}
          {showWelcomeCard && <UserListWelcomeCard />}
        </InfiniteScroll>
        {showManualLoadMore && (
          <Box display="flex" justifyContent="center" p={2}>
            <Button variant="outlined" onClick={handleLoadMore}>
              {t('userAccessControl.actions.loadMore')}
            </Button>
          </Box>
        )}
        {!isLoading && !hasMore && visibleUsers.length === 0 && (
          <SearchNoResult searchText={searchText} onClearSearch={() => setSearchText('')} />
        )}
      </List>
      {isAllowedToAddUser && (
        <FloatingBottomButton
          icon={<PersonAdd />}
          onAction={() => navigate(userAccessRoutes.createUser)}
          position="absolute"
          title={t('userAccessControl.newUser')}
        />
      )}
    </Stack>
  );
}
